/**
 * Locale resolution + {placeholder} interpolation.
 *
 * One component tree, two dictionaries. Components read copy through
 * useCopy(); facts come separately from useShopConfig(). Neither
 * dictionary contains facts — see docs/COPY_STYLE.md.
 */

import { createContext, useContext } from 'react';
import type { CopyDict } from './types';
import { en } from './copy.en';
import { es } from './copy.es';

export type Locale = 'en' | 'es';

export const DICTIONARIES: Record<Locale, CopyDict> = { en, es };

export const LocaleContext = createContext<Locale>('en');

/** Current locale, set by the route tree (/ = en, /es = es). */
export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/** The active dictionary. */
export function useCopy(): CopyDict {
  return DICTIONARIES[useLocale()];
}

/**
 * Replace {placeholder} tokens with resolved facts.
 *
 * Unknown tokens are left intact rather than blanked — a visible "{foo}"
 * in staging is a bug report; an empty string is a silent hole in the copy.
 */
export function interpolate(
  template: string,
  facts: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in facts ? String(facts[key]) : whole
  );
}

/** Locale prefix for building hrefs: '' for en, '/es' for es. */
export function localePrefix(locale: Locale): string {
  return locale === 'es' ? '/es' : '';
}

export type { CopyDict } from './types';
export { en, es };
